import { json } from '@remix-run/node';
import { useLoaderData, useSearchParams } from '@remix-run/react';
import type { LoaderFunctionArgs, MetaFunction } from '@remix-run/node';
import { motion } from 'framer-motion';
import { PageHeader } from '~/components/PageHeader';
import { BlogFilterBar } from '~/components/blog/BlogFilterBar';
import { BlogCard } from '~/components/blog/BlogCard';
import { Pagination } from '~/components/blog/Pagination';
import {
  getAllPosts,
  getAllCategories,
  getAllTags,
  getPostsByCategory,
  getPostsByTag,
  searchPosts
} from '~/lib/blog.static';
import { buildMeta } from '~/lib/seo';
import { Container } from '~/components/ui/container';
import { staggerContainer, staggerItem, fadeUp } from '~/lib/animations';
import { useAnimationState } from '~/lib/motion-utils';

const POSTS_PER_PAGE = 9;

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const category = url.searchParams.get('category');
  const tagsParam = url.searchParams.get('tags');
  const search = url.searchParams.get('q');
  const page = Number(url.searchParams.get('page')) || 1;

  // Parse tags from comma-separated string
  const tags = tagsParam ? tagsParam.split(',').filter(Boolean) : [];

  // Get posts based on filters
  let posts;
  if (category) {
    posts = await getPostsByCategory(category);
  } else if (tags.length > 0) {
    // Get posts that match ANY of the selected tags (OR logic)
    const allPosts = await getAllPosts();
    posts = allPosts.filter(post => {
      const postTags = post.tags || [];
      return tags.some(tag => postTags.includes(tag));
    });
  } else if (search) {
    posts = await searchPosts(search);
  } else {
    posts = await getAllPosts();
  }

  // Sort by order (descending - highest number first)
  posts.sort((a, b) => {
    const orderA = a.order ?? 0;
    const orderB = b.order ?? 0;
    return orderB - orderA;
  });

  // Pagination
  const totalPosts = posts.length;
  const totalPages = Math.ceil(totalPosts / POSTS_PER_PAGE);
  const startIndex = (page - 1) * POSTS_PER_PAGE;
  const endIndex = startIndex + POSTS_PER_PAGE;
  const paginatedPosts = posts.slice(startIndex, endIndex);

  // Get categories and tags for sidebar
  const categories = getAllCategories();
  const allTags = getAllTags();

  return json({
    posts: paginatedPosts,
    categories,
    tags: allTags,
    pagination: {
      currentPage: page,
      totalPages,
      totalPosts,
    },
    activeFilters: {
      category,
      tags,
      search
    }
  });
}

export const meta: MetaFunction = () =>
  buildMeta({
    title: 'Developer Blog - Lumora',
    description: 'News, updates, and insights from the Lumora development team',
  });

export default function BlogIndexPage() {
  const { posts, categories, tags, pagination, activeFilters } = useLoaderData<typeof loader>();
  const [searchParams] = useSearchParams();
  const { getVariants } = useAnimationState();

  const currentCategory = activeFilters.category;
  const currentTags = activeFilters.tags;
  const currentPage = pagination.currentPage;

  return (
    <Container size="default" padding="large">
      <motion.div
        className="opacity-0"
        initial="hidden"
        animate="visible"
        variants={getVariants(fadeUp)}
      >
        <PageHeader title="Developer Blog" />
      </motion.div>

      {/* Filter Bar */}
      <motion.div
        className="opacity-0"
        initial="hidden"
        animate="visible"
        variants={getVariants({
          ...fadeUp,
          visible: {
            ...fadeUp.visible,
            transition: {
              ...(typeof fadeUp.visible === 'object' && 'transition' in fadeUp.visible
                ? fadeUp.visible.transition
                : {}),
              delay: 0.1,
            },
          },
        })}
      >
        <BlogFilterBar
          categories={categories}
          tags={tags}
          currentCategory={currentCategory}
          currentTags={currentTags}
        />
      </motion.div>

      {/* Main Content - Single Column Full Width */}
      <div className="w-full">
        {/* Post List - Full Width Stacked */}
        {posts.length > 0 ? (
          <>
            <motion.div
              className="space-y-6 opacity-0"
              initial="hidden"
              animate="visible"
              variants={getVariants({
                ...staggerContainer,
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.08,
                    delayChildren: 0.2,
                  },
                },
              })}
            >
              {posts.map((post) => (
                <motion.div key={post.slug} variants={getVariants(staggerItem)}>
                  <BlogCard post={post} variant="horizontal" />
                </motion.div>
              ))}
            </motion.div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <motion.div
                className="mt-12 opacity-0"
                initial="hidden"
                animate="visible"
                variants={getVariants({
                  ...fadeUp,
                  visible: {
                    ...fadeUp.visible,
                    transition: {
                      ...(typeof fadeUp.visible === 'object' && 'transition' in fadeUp.visible
                        ? fadeUp.visible.transition
                        : {}),
                      delay: 0.3,
                    },
                  },
                })}
              >
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  baseUrl="/blog"
                />
              </motion.div>
            )}
          </>
        ) : (
          <motion.div
            className="text-center py-12 opacity-0"
            initial="hidden"
            animate="visible"
            variants={getVariants(fadeUp)}
          >
            <p className="text-muted-foreground mb-4">
              {activeFilters.search
                ? `No posts found for "${activeFilters.search}"`
                : currentTags.length > 0
                ? `No posts found with the selected tag${currentTags.length > 1 ? 's' : ''}`
                : 'No posts found'}
            </p>
            <a href="/blog" className="text-primary hover:underline">
              View all posts →
            </a>
          </motion.div>
        )}
      </div>
    </Container>
  );
}